import type { BarSeriesOption, EChartsOption } from 'echarts';
import { alpha } from '@mui/material/styles';

import { buildAxisLabelFormatter } from '../shared/axis';
import { buildAnimationConfig } from '../shared/animation';
import { BOTH_RADIUS } from '../shared/barRadius';
import { buildTooltipConfig, buildTooltipHeader, buildTooltipTotalRow, buildTooltipWrapper } from '../shared/tooltip';
import { buildComparisonBarTooltipFormatter } from '../comparisonBar/tooltip';
import { buildComparisonHatchDecal, COMPARISON_FILL_ALPHA } from '../comparisonBar/decal';
import { buildComparisonWaterfallLayout } from './layout';
import type { ComparisonWaterfallChartConfig, ComparisonWaterfallLayout, ComparisonWaterfallRow } from './types';

export type { ComparisonWaterfallChartConfig, ComparisonWaterfallLayout, ComparisonWaterfallRow } from './types';

const WATERFALL_BAR_WIDTH = 18;
const WATERFALL_LABEL_GAP = 8;
const WATERFALL_LABEL_DOMAIN_PADDING = 0.5;
const TOTAL_IDS = ['comparison-total', 'current-total'] as const;
const LABEL_WEIGHT_TOTAL = 600;
const LABEL_WEIGHT_STEP = 500;

const amount = (value: number | null | undefined): number => value ?? 0;
const domainPadding = (min: number, max: number): number => Math.max(max - min, 1) * WATERFALL_LABEL_DOMAIN_PADDING;
const waterfallAxisMin = ({ min, max }: { min: number; max: number }): number => {
  if (min === 0 && max === 0) return -1;
  return min < 0 ? min - domainPadding(min, max) : 0;
};
const waterfallAxisMax = ({ min, max }: { min: number; max: number }): number => {
  if (min === 0 && max === 0) return 1;
  return max > 0 ? max + domainPadding(min, max) : 0;
};

type WaterfallStep = {
  base: number;
  value: number;
  color: string;
  label: string;
  direction: ComparisonWaterfallRow['deltaDirection'];
  total: boolean;
};

export const buildComparisonWaterfallSteps = (
  rows: ComparisonWaterfallRow[],
  config: ComparisonWaterfallChartConfig,
): WaterfallStep[] => {
  const start = amount(config.totalValues[1]);
  const end = amount(config.totalValues[0]);
  let cumulative = start;

  const deltas = rows.map((row) => {
    const delta = row.deltaAmountSigned ?? amount(row.values[0]) - amount(row.values[1]);
    const next = cumulative + delta;
    const step = {
      base: cumulative,
      value: delta,
      color: row.color,
      label: row.deltaText,
      direction: row.deltaDirection,
      total: false,
    };
    cumulative = next;
    return step;
  });

  const totalColor = config.theme.colors[0] ?? config.theme.textColor;
  return [
    {
      base: 0,
      value: start,
      color: totalColor,
      label: config.totalLabels[1] ?? '',
      direction: null,
      total: true,
    },
    ...deltas,
    {
      base: 0,
      value: end,
      color: totalColor,
      label: config.totalLabels[0] ?? '',
      direction: null,
      total: true,
    },
  ];
};

export const buildComparisonWaterfallChartOption = (
  rows: ComparisonWaterfallRow[],
  config: ComparisonWaterfallChartConfig,
): { option: EChartsOption; layout: ComparisonWaterfallLayout } => {
  const { animated = true, labelWidthPx, privacyMask, ranges, theme } = config;
  const displayedRanges = ranges.slice(0, 2);
  const steps = buildComparisonWaterfallSteps(rows, config);
  const categories = [
    displayedRanges[1]?.label ?? 'Comparison',
    ...rows.map((row) => row.name),
    displayedRanges[0]?.label ?? 'Current',
  ];
  const rowTooltip = buildComparisonBarTooltipFormatter(rows, { ...config, ranges: displayedRanges }, theme);
  const layout = buildComparisonWaterfallLayout(categories.length, labelWidthPx);

  const helperData = steps.map((step) => ({
    value: step.total ? null : step.base,
    itemStyle: { color: 'transparent' },
  }));
  const labelPositionFor = (value: number): 'left' | 'right' => (value < 0 ? 'left' : 'right');
  const privateLabel = (label: string): string => (label === '' || label === '—' ? label : String(privacyMask(label)));
  const labelFor = (step: WaterfallStep) => {
    const arrow = step.direction === 'up' ? '↑' : step.direction === 'down' ? '↓' : '';
    return {
      show: true,
      position: labelPositionFor(step.value),
      distance: WATERFALL_LABEL_GAP,
      color: step.total ? theme.textColor : step.color,
      fontSize: 14,
      fontWeight: step.total ? LABEL_WEIGHT_TOTAL : LABEL_WEIGHT_STEP,
      formatter: () => {
        const label = privateLabel(step.label);
        return arrow ? `${arrow} ${label}` : label;
      },
    };
  };
  const deltaData = steps.map((step, index) => {
    if (step.total) return null;
    return {
      value: step.value,
      itemStyle: { color: step.color, borderRadius: BOTH_RADIUS },
      label: labelFor(step),
      id: rows[index - 1]?.id,
    };
  });
  const totalData = steps.map((step, index) => {
    if (!step.total) return null;
    const isComparisonTotal = index === 0;
    return {
      value: step.value,
      itemStyle: {
        color: isComparisonTotal ? alpha(step.color, COMPARISON_FILL_ALPHA) : step.color,
        borderRadius: BOTH_RADIUS,
        ...(isComparisonTotal ? { decal: buildComparisonHatchDecal(step.color, 1) } : {}),
      },
      label: labelFor(step),
      id: isComparisonTotal ? TOTAL_IDS[0] : TOTAL_IDS[1],
    };
  });

  const totalTooltip = (dataIndex: number): string => {
    const rangeIndex = dataIndex === 0 ? 1 : 0;
    const range = displayedRanges[rangeIndex];
    const totalLabel = config.totalLabels[rangeIndex];
    const formattedTotal = totalLabel == null || totalLabel === '—' ? '—' : String(privacyMask(totalLabel));
    return buildTooltipWrapper(
      `${buildTooltipHeader(range?.label ?? '', theme)}${buildTooltipTotalRow('Total', formattedTotal, theme)}`,
    );
  };

  const series: BarSeriesOption[] = [
    {
      id: 'comparison-waterfall-helper',
      type: 'bar',
      stack: 'waterfall',
      stackStrategy: 'all',
      data: helperData,
      barWidth: WATERFALL_BAR_WIDTH,
      silent: true,
      animation: false,
      tooltip: { show: false },
      emphasis: { disabled: true },
    },
    {
      id: 'comparison-waterfall-value',
      type: 'bar',
      stack: 'waterfall',
      stackStrategy: 'all',
      data: deltaData,
      barWidth: WATERFALL_BAR_WIDTH,
      barMinWidth: 1,
      animationDuration: animated ? 480 : 0,
      animationDelay: (dataIndex: number) => (animated ? Math.min(dataIndex, 40) * 36 : 0),
      emphasis: { disabled: true },
    },
    {
      id: 'comparison-waterfall-total',
      type: 'bar',
      data: totalData,
      barWidth: WATERFALL_BAR_WIDTH,
      // Totals and floating changes are sparse series on the same category axis. Overlay their
      // category slots so both kinds of bars share the exact center of the label beside them.
      barGap: '-100%',
      animationDuration: animated ? 480 : 0,
      emphasis: { disabled: true },
    },
  ];

  return {
    layout,
    option: {
      ...buildAnimationConfig(animated),
      animationDurationUpdate: animated ? 300 : 0,
      textStyle: { fontFamily: theme.fontFamily },
      grid: {
        left: layout.gridLeft,
        right: layout.gridRight,
        top: layout.gridTop,
        height: layout.gridHeight,
        bottom: layout.gridBottom,
      },
      tooltip: {
        ...buildTooltipConfig(theme),
        trigger: 'axis',
        axisPointer: { type: 'shadow', axis: 'y', shadowStyle: { opacity: 0 } },
        formatter: (params: unknown) => {
          const first = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number } | undefined;
          const dataIndex = first?.dataIndex;
          if (typeof dataIndex !== 'number') return '';
          if (dataIndex === 0 || dataIndex === rows.length + 1) return totalTooltip(dataIndex);
          return rowTooltip([{ dataIndex: dataIndex - 1, seriesId: 'comparison-waterfall-value' }]);
        },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: categories,
        axisLabel: {
          color: theme.textColor,
          fontSize: 14,
          align: 'left',
          width: layout.labelWidth,
          overflow: 'break',
          lineHeight: 18,
          margin: layout.labelMargin,
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      xAxis: {
        type: 'value',
        // Keep zero in view and reserve room beyond both signed extremes for the outside labels.
        min: waterfallAxisMin,
        max: waterfallAxisMax,
        axisLabel: {
          color: theme.textSecondaryColor,
          fontSize: 12,
          formatter: buildAxisLabelFormatter({
            formatAmount: config.formatAmount,
            privacyMask,
            useNumberPrefix: true,
          }),
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: theme.gridColor } },
      },
      series,
    },
  };
};
